import React, { useState, useCallback, useEffect } from 'react';
import moment from 'moment';

import dashboardApi from 'services/apis/dashboard';
import { Table, Text, Link, Button } from 'v3/components';
import { formatDateTime } from 'v3/utils/formatter';
import { useQuery } from 'hooks';

import Card, { Number, Description, SeeAll } from './Card';

export default function LoadsInNegotiation({ dashboard, name, id }) {
  const [isFetching, setFetching] = useState(false);
  const [data, setData] = useState({});
  const [filter, setFilter] = useState({});
  const [currentPage, setCurrentPage] = useState({});
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(false);
  const queryParams = useQuery();

  function setQueryParams(filters) {
    const { tags = [] } = filters;
    queryParams.delete('tags');

    if (tags) {
      tags.forEach((tag) => {
        queryParams.append('tags', tag.id);
      });
    }

    if (tags?.length === 0) {
      queryParams.set('no-tags', true);
    } else {
      queryParams.delete('no-tags');
    }
  }

  const fetchLoadsNegotiator = useCallback(async (filters = {}) => {
    try {
      setLoading(true);

      const {
        tags = [],
        clients = [],
        costCenters = [],
        pcps = [],
        companies = [],
        page = 1,
      } = filters;

      if (page === 1) {
        setFetching(true);
      }
      setQueryParams(filters);
      setFilter(filters);
      setCurrentPage(+page);

      const response = await dashboardApi.get('/loads-waiting-negotiation', {
        params: {
          tags: tags?.map((tag) => tag.id),
          clients: clients?.map((client) => client.id),
          page,
          costCenters: costCenters?.map((costCenter) => costCenter?.id),
          pcps: pcps?.map((pcp) => pcp?.id),
          companies: companies?.map((company) => company?.id),
        },
      });

      setPagination(response.data?.last?.pagination);

      if (page === 1) {
        setData(response.data);
      } else {
        setData((oldData) => ({
          ...oldData,
          last: {
            ...oldData.last,
            data: [...oldData.last.data, ...response.data.last.data],
          },
        }));
      }
    } catch (ex) {
      // Handle exception
    } finally {
      setFetching(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const startDate = moment().subtract(3, 'days').format('YYYY-MM-DD');
    queryParams.set('startDate', startDate);
  }, []);

  return (
    <Card
      title="Cargas aguardando atendimento"
      isLoading={isFetching}
      fetchFn={fetchLoadsNegotiator}
      onFiltersChanged={fetchLoadsNegotiator}
      dashboard={dashboard}
      name={name}
      id={id}
      excludeFilterByNegotiator
      excludeFilterByDate
      excludeFilterByBranchOffice
      ListComponent={
        <>
          <Table striped>
            <thead>
              <tr>
                <th>
                  <Text weight="500" color="dark">
                    Centro de custo
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Empresa
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Tags da carga
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Negociação
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Carga
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Carregamento
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Origem
                  </Text>
                </th>
                <th>
                  <Text weight="500" color="dark">
                    Destino
                  </Text>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.last?.data?.map((load) => {
                const title = `Cliente ${load.social_name} - ${load.cgccpf}`;
                return (
                  <tr>
                    <td>
                      <Text type="regular" color="gray">
                        {load.cost_center || '-'}
                      </Text>
                    </td>
                    <td>
                      <Text type="regular" color="gray">
                        {load?.company_name || '-'}
                      </Text>
                    </td>
                    <td>
                      <Text type="regular" color="gray">
                        {load.load_tags || '-'}
                      </Text>
                    </td>
                    <td>
                      <Link
                        to={`/negociacoes?deal_id=${load.deal_id}`}
                        title={title}
                        target="_blank"
                      >
                        {load.deal_id}
                      </Link>
                    </td>
                    <td>
                      <Link to={`/cargas/${load.id}`} title={title} target="_blank">
                        {load.id}
                      </Link>
                    </td>
                    <td>
                      <Text type="regular" color="gray">
                        {formatDateTime(load.scheduled_origin)}
                      </Text>
                    </td>
                    <td>
                      <Text type="regular" color="gray">
                        {load.city_origin} - {load.uf_origin}
                      </Text>
                    </td>
                    <td>
                      <Text type="regular" color="gray">
                        {load.city_destination} - {load.uf_destination}
                      </Text>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          {pagination.lastPage > 1 && pagination.to < pagination.total && (
            <div className="text-center mt-3 col-12">
              <Button
                className="mt-2 ml-auto align-item-center"
                onClick={() => {
                  fetchLoadsNegotiator({
                    page: currentPage + 1,
                    tags: filter?.tags,
                    clients: filter?.clients,
                    costCenters: filter?.costCenters,
                    pcps: filter?.pcps,
                    companies: filter?.companies,
                  });
                }}
                loading={loading}
              >
                <Text type="regular" color="white" truncate>
                  Ver mais
                </Text>
              </Button>
            </div>
          )}
          {!!data?.total && (
            <SeeAll to={{ pathname: '/negociacoes', search: queryParams.toString() }}>
              ver todas as {data?.total} negociaçõess
            </SeeAll>
          )}
        </>
      }
      NumericComponent={
        <div className="d-flex justify-content-center text-center flex-column">
          <Number color="error">{data?.total}</Number>
          <Description>cargas aguardando atendimento</Description>
        </div>
      }
    />
  );
}
